import { useEffect, useState } from "react";
import {
	DeepPartial,
	FieldName,
	FieldValue,
	FieldValues,
	InternalFieldName,
	UnpackNestedValue,
	useForm,
} from "react-hook-form";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router";
import { capitalize } from "shared/components/Inputs/InputHelpers";
import { trimObject } from "../../features/Helpers";
import { show } from "../components/Toast/toastSlice";

type CustomSubmitProps<T, SF extends AnyPromise> = [
	submitFunction: (data: T) => Promise<GetResponseForPromise<SF>>,
	data: T,
	toastMessage?: string,
	returnResponse?: boolean,
	onSuccess?: (res: GetResponseForPromise<SF>) => void | Promise<void>,
	onFailure?: (err: Record<string, string[]>) => void,
	options?: {
		isArray?: boolean;
		showToastError?: boolean;
	}
];
type GetResponseForPromise<Prom> = Prom extends (...data: any[]) => Promise<infer Res> ? Res : never;
type AnyPromise = (...data: any[]) => Promise<any>;

const getMessageForType = (name: string, errorType: string) => {
	switch (errorType) {
		case "required":
			return `${capitalize(name)} is required.`;
		case "minLength":
			return `${capitalize(name)} is not long enough.`;
		case "maxLength":
			return `${capitalize(name)} is too long.`;
		case "pattern":
			return `${capitalize(name)} is invalid.`;
	}
};

export function useFormHook<T extends FieldValues = FieldValues>(
	defaultValues?: UnpackNestedValue<DeepPartial<T>>,
	useToastErrors?: boolean
) {
	const history = useHistory();
	const formHook = useForm<T>({
		defaultValues: defaultValues,
	});
	const dispatch = useDispatch();
	const [formErrors, setFormErrors] = useState<Partial<Record<string, string[]>>>();
	const [dirty, setDirty] = useState(false);
	const defaultSubmit = formHook.handleSubmit;

	const trimmedFormValues = (data: any) => {
		return trimObject(data);
	};

	useEffect(() => {
		setDirty(formHook.formState.isDirty);
	}, [formHook.formState.isDirty]);

	useEffect(() => {
		if (!useToastErrors) return;
		const firstKey = Object.keys(formHook.errors)[0];
		if (!firstKey) return;

		const error: any = formHook.errors[firstKey];
		if (error?.type) {
			const message = getMessageForType(firstKey, error.type);
			dispatch(show({ message, style: "error" }));
		}
	}, [formHook.errors]);

	const customSubmit = <T, SF extends AnyPromise>(...props: CustomSubmitProps<T, SF>) => {
		const [submitFunction, data, toastMessage, returnResponse, onSuccess, onFailure, options] = props;

		if (Object.keys(formHook.errors).length === 0) {
			return submitFunction({ ...trimmedFormValues(data) })
				.then((res: any) => {
					if (toastMessage) {
						dispatch(
							show({
								message: toastMessage,
							})
						);
					}
					setDirty(false);
					setFormErrors({});
					if (onSuccess) onSuccess(res);
					if (returnResponse) return res;
					history.goBack();
				})
				.catch((errors) => {
					setFormErrors(errors?.response?.data?.errors);
					onFailure?.(errors?.response?.data?.errors);
				});
		}
	};

	formHook.handleSubmit = <SubmitFieldValues extends FieldValues = T>(
		...params: Parameters<typeof defaultSubmit<SubmitFieldValues>>
	): any => {
		const [onValid, onInvalid] = params;
		return defaultSubmit(onValid);
	};

	return {
		formHook,
		formErrors,
		setFormErrors,
		customSubmit,
		dirty,
		setDirty,
	};
}
